import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import BrandPopup from '../../components/admin/BrandPopup';

const AdminBrands = () => {
  const [brands,      setBrands]      = useState([]);
  const [regions,     setRegions]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [popup,       setPopup]       = useState(null); // null | 'new' | brand object
  const [saving,      setSaving]      = useState(false);
  const toast = useToast();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [b, r] = await Promise.all([api.getBrands(), api.getRegions()]);
      setBrands(b.data);
      setRegions(r.data);
    } catch (err) {
      toast.error('Errore nel caricamento dei dati.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (popup === 'new') {
        await api.createBrand(formData);
        toast.success('Brand creato con successo.');
      } else {
        await api.updateBrand(popup.id, formData);
        toast.success('Brand aggiornato con successo.');
      }
      setPopup(null);
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Errore durante il salvataggio.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await api.deleteBrand(popup.id);
      toast.success('Brand eliminato.');
      setPopup(null);
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Errore durante l\'eliminazione.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (brand) => {
    try {
      await api.toggleBrand(brand.id);
      fetchData();
    } catch (err) {
      toast.error('Errore durante l\'aggiornamento.');
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-64">
        <div className="text-gray-500 text-sm">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 md:mb-8">
        <div>
          <h1 className="text-2xl text-white font-semibold">Brand</h1>
          <p className="text-gray-500 text-sm mt-0.5">{brands.length} brand totali</p>
        </div>
        <button
          onClick={() => setPopup('new')}
          className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded-lg text-sm font-medium hover:bg-gray-100 transition"
        >
          <span className="text-lg leading-none">+</span>
          Nuovo Brand
        </button>
      </div>

      {/* Grid brand */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        {brands.map(brand => (
          <div
            key={brand.id}
            className={`relative group bg-black border-2 rounded-lg p-2 transition ${
              brand.is_active ? 'border-white' : 'border-gray-700 opacity-50'
            }`}
          >
            {/* Logo */}
            <div className="h-28 flex items-center justify-center">
              {brand.logo ? (
                <img
                  src={brand.logo}
                  alt={brand.name}
                  className="w-full h-full object-contain grayscale"
                />
              ) : (
                <div className="text-gray-600 text-xs text-center px-2">
                  Nessun logo
                </div>
              )}
            </div>

            {/* Nome */}
            <p className="text-center text-xs text-gray-400 mt-1 truncate">{brand.name}</p>

            {/* Badge inattivo */}
            {!brand.is_active && (
              <div className="absolute top-1.5 left-1.5">
                <span className="text-xs bg-gray-800 text-gray-400 px-1.5 py-0.5 rounded">
                  Inattivo
                </span>
              </div>
            )}

            {/* Overlay azioni — solo desktop (hover) */}
            <div className="absolute inset-0 bg-black/70 rounded-lg opacity-0 group-hover:opacity-100 transition hidden md:flex flex-col items-center justify-center gap-2">
              <button
                onClick={() => setPopup(brand)}
                className="px-3 py-1.5 bg-white text-black rounded-md text-xs font-medium hover:bg-gray-100 transition"
              >
                Modifica
              </button>
              <button
                onClick={() => handleToggle(brand)}
                className="px-3 py-1.5 border border-gray-600 text-gray-300 rounded-md text-xs hover:border-white hover:text-white transition"
              >
                {brand.is_active ? 'Disattiva' : 'Attiva'}
              </button>
            </div>

            {/* Azioni sempre visibili — solo mobile (no hover sul tap) */}
            <div className="flex md:hidden gap-1.5 mt-2">
              <button
                onClick={() => setPopup(brand)}
                className="flex-1 px-2 py-1.5 bg-white text-black rounded-md text-xs font-medium"
              >
                Modifica
              </button>
              <button
                onClick={() => handleToggle(brand)}
                className="px-2 py-1.5 border border-gray-600 text-gray-300 rounded-md text-xs"
              >
                {brand.is_active ? 'Off' : 'On'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Popup */}
      {popup !== null && (
        <BrandPopup
          brand={popup === 'new' ? null : popup}
          regions={regions}
          onClose={() => setPopup(null)}
          onSave={handleSave}
          onDelete={handleDelete}
          loading={saving}
        />
      )}
    </div>
  );
};

export default AdminBrands;
